import { useState, useCallback, useEffect } from "react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
} from "antd";
import dayjs from "dayjs";

import { FormFilters } from "../../types/formFilters";
import { criteriaLabelMap } from "../Forms/utils";

import FiltersFormItem from "./FiltersFormItem";
import ModalFormContainer from "./ModalFormContainer";

import styles from "./FiltersForm.module.css";
import { CRITERIA_OPTIONS, FILTERED_STATUS_OPTIONS, VALIDATION_OPTIONS } from "../../utils/constants";

const { Option } = Select;
const { RangePicker } = DatePicker;

interface TaskFiltersFormProps {
  visible: boolean;
  onCancel: () => void;
  onApply: (filters: FormFilters) => void;
  initialFilters?: FormFilters;
}

const TaskFiltersForm = ({
  visible,
  onCancel,
  onApply,
  initialFilters = {
    taskId: { from: null, to: null },
    product: undefined,
    user: undefined,
    procedureType: undefined,
    procedureId: undefined,
    creationDate: null,
    editionDate: null,
    validation: [],
    criteria: [],
    status: [],
    taskValidation: []
  }
}: TaskFiltersFormProps) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    handleClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, initialFilters]);

  const handleApply = useCallback(async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      const formFilters: FormFilters = {
        ...initialFilters,
        criteria: values.criteria || [],
        creationDate: values.creationDate,
        status: values.status || [],
        taskValidation: values.taskValidation || []
      };
      
      onApply(formFilters);
      onCancel();
    } catch (error) {
      console.error('Error validating form:', error);
    } finally {
      setLoading(false);
    }
  }, [form, initialFilters, onApply, onCancel]);

  const handleClear = useCallback(() => {
    form.setFieldsValue({
      criteria: initialFilters.criteria || [],
      creationDate: initialFilters.creationDate,
      status: initialFilters.status || [],
      taskValidation: initialFilters.taskValidation || []
    });
  }, [form, initialFilters]);

  const handleCancel = useCallback(() => {
    handleClear();
    onCancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, initialFilters, onCancel]);

  return (
    <ModalFormContainer
      open={visible}
      onCancel={handleCancel}
    >
      <Form 
        form={form} 
        className={styles.form}
        initialValues={{
          criteria: initialFilters.criteria || [],
          creationDate: initialFilters.creationDate,
          status: initialFilters.status || [],
          taskValidation: initialFilters.taskValidation || []
        }}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={24}>
            <FiltersFormItem label="Criteris" name="criteria">
              <Select
                mode="multiple"
                placeholder="Selecciona criteris"
                allowClear
              >
                {CRITERIA_OPTIONS.map((criteria) => (
                  <Option key={criteria.value} value={criteria.value}>
                    {criteriaLabelMap[criteria.value] || criteria.value}
                  </Option>
                ))}
              </Select>
            </FiltersFormItem>
          </Col>
        </Row>

        <FiltersFormItem label="Data de la tasca" name="creationDate">
          <RangePicker
            className="w-full"
            maxDate={dayjs()}
            placeholder={['Data inici', 'Data fi']}
            format="DD/MM/YYYY"
          />
        </FiltersFormItem>

        <FiltersFormItem label="Estat de la tasca" name="status">
          <Checkbox.Group>
            <Row>
              {FILTERED_STATUS_OPTIONS.map((status) => (
                <Col  key={status.value}>
                  <Checkbox value={status.value}>
                    {status.label}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </FiltersFormItem>

        <FiltersFormItem label="Validació" name="taskValidation">
          <Checkbox.Group
            options={VALIDATION_OPTIONS}
          />
        </FiltersFormItem>

        <footer className={styles.footer}>
          <Button
            onClick={handleCancel}
            className={styles.footerBtn}
            type="text"
          >
            Cancel·lar
          </Button>
          <Button
            loading={loading}
            onClick={handleApply}
            className={styles.footerBtn}
            type="primary"
          >
            Aplicar
          </Button>
        </footer>
      </Form>
    </ModalFormContainer>
  );
};

export default TaskFiltersForm;